import React from 'react'
import { FormattedMessage } from 'react-intl'
import capitalizeString from 'utils/capitalize'
import RejectIcon from 'assets/icons/reject-icon'
import { reassessmentTriggerReasons } from 'constants/reassessmentTriggerReasons'
import Tooltip from 'components/Tooltip'
import { RedListCategoryStatus, TableNotQualifyIconWrapper } from './style'
import capitalizeFirstLetterString from 'utils/capitalizeFirstLetter'
import { specieAssessmentParameterType } from 'constants/speciesParameterType'

export const renderRedListCategoryStatus = (status) => {
  const color = {
    EX: '#FFFFFF',
    EW: 'rgba(84, 35, 68, 0.3)',
    CR: 'rgba(216, 30, 5, 0.5)',
    EN: 'rgba(252, 127, 63, 0.6)',
    VU: 'rgba(249, 232, 20, 0.6)',
    LR: 'rgba(226, 211, 105, 0.8)',
    NT: 'rgba(204, 226, 38, 0.6)',
    LC: 'rgba(96, 198, 89, 0.6)',
    DD: '#D1D1C6'
  }

  return (
    <RedListCategoryStatus color={color[status]}>
      <FormattedMessage id={status} />
    </RedListCategoryStatus>
  )
}

const tooltipXOffset = 0
const tooltipYOffset = -16

const formattedRejectIconTooltipHint = (item) => {
  const hints = []
  for (const element of item || []) {
    const match = reassessmentTriggerReasons.find((elementId) => elementId.id === element)
    if (match?.name) {
      hints.push(match.name)
    }
  }
  return hints
}

const notQulifyIcon = (hint) => {
  return (
    <Tooltip
      kbaCriteria={true}
      style={{ zIndex: 1000 }}
      placement='top'
      trigger={['hover']}
      align={{ offset: [tooltipXOffset, tooltipYOffset] }}
      overlay={{ label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint(hint).join(', ') }}
    >
      <TableNotQualifyIconWrapper>
        <RejectIcon />
      </TableNotQualifyIconWrapper>
    </Tooltip>

  )
}

const getQualifies = (item, site, disabled) => {
  if (disabled) {
    return item.triggerReasonId ? item.triggerReasonId.includes(1) : true
  }
  return site.reassessmentFlag ? (item.triggerReasonId?.includes(1) ?? true) : true
}

const getAssessmentParameterTypeIds = (parameters = []) => {
  return [...new Set(parameters.map((parameter) => parameter.SiteAssesmentParameterTypeId))]
}

const shouldIncludeParameterType = (item, parameterTypeId) => {
  if (item.siteAssesmentParameterTypeId == null) {
    return true
  }
  return item.siteAssesmentParameterTypeId.some((itemParameterTypeId) => itemParameterTypeId == parameterTypeId)
}

const getPopulationDisplay = (assess, qualifies) => {
  if (!qualifies) {
    return '-'
  }

  const bestSite = Number(assess?.bestSite)
  const bestGlobal = Number(assess?.bestGlobal)
  const rawPopulation = (bestSite / bestGlobal) * 100
  const isInvalidPopulation = (
    rawPopulation === Number.NEGATIVE_INFINITY ||
    rawPopulation === Number.POSITIVE_INFINITY ||
    Number.isNaN(rawPopulation)
  )

  if (isInvalidPopulation) {
    return '-'
  }

  return `${rawPopulation.toFixed(2)}%`
}

const getIbaCriteriaDisplay = (item, isAves, site, canSeeSiteIBACriteria, shouldShowSiteIBAData) => {
  if (!canSeeSiteIBACriteria(site)) {
    return null
  }

  const ibaValue = isAves ? capitalizeString(item.ibaCriterias) : null
  return shouldShowSiteIBAData(site, ibaValue, 'sitesManagement')
}

const makeRowObject = ({
  item,
  parameterLabel,
  key,
  qualifies,
  population,
  isAves,
  site,
  canSeeSiteIBACriteria,
  shouldShowSiteIBAData
}) => {
  return {
    key,
    taxonGroup: `${capitalizeString(item.taxonGroup)}`,
    season: qualifies ? capitalizeString(item.season?.name || '-') : '-',
    scientificName: `${capitalizeFirstLetterString(item.scientificName)}`,
    commonName: `${capitalizeFirstLetterString(item.commonName)}`,
    iucnStatus: renderRedListCategoryStatus(item.iucnStatus),
    reproductiveUnits: qualifies ? item.reproductiveUnits : '-',
    population,
    kbaCriteria: qualifies ? `${capitalizeString(item.kbaCriteria)}` : notQulifyIcon(item.triggerReasonId),
    ibaCriterias: getIbaCriteriaDisplay(
      item,
      isAves,
      site,
      canSeeSiteIBACriteria,
      shouldShowSiteIBAData
    ),
    assessmentParameter: capitalizeFirstLetterString(parameterLabel) || '-'
  }
}

const appendRowsForItem = ({
  item,
  site,
  qualifies,
  isAves,
  rows,
  existingKeys,
  canSeeSiteIBACriteria,
  shouldShowSiteIBAData
}) => {
  for (const criteria of site.kbaCriteria) {
    if (criteria.scientificName !== item.scientificName) {
      continue
    }

    const parameters = criteria.speciesAssesmentParametersDisplay || []
    const assessmentParameterTypeIds = getAssessmentParameterTypeIds(parameters)

    for (const parameterTypeId of assessmentParameterTypeIds) {
      if (!shouldIncludeParameterType(item, parameterTypeId)) {
        continue
      }

      const parameterLabel = specieAssessmentParameterType.find((param) => param.id === parameterTypeId)?.name
      const key = `${item.taxonGroup}-${item.season?.name || '-'}-${item.scientificName}-${parameterLabel}`

      if (existingKeys.has(key)) {
        continue
      }
      existingKeys.add(key)

      const assess = parameters.find((param) => param.SiteAssesmentParameterTypeId === parameterTypeId)
      const population = getPopulationDisplay(assess, qualifies)

      rows.push(makeRowObject({
        item,
        parameterLabel,
        key,
        qualifies,
        population,
        isAves,
        site,
        canSeeSiteIBACriteria,
        shouldShowSiteIBAData
      }))
    }
  }
}

export const makeCalculationRow = (calculationData, site, disabled, canSeeSiteIBACriteria, shouldShowSiteIBAData) => {
  const rows = []
  const existingKeys = new Set()

  if (!site?.kbaCriteria) {
    return rows
  }

  for (const item of calculationData) {
    const qualifies = getQualifies(item, site, disabled)
    const isAves = item.taxonGroup === 'Aves'
    appendRowsForItem({
      item,
      site,
      qualifies,
      isAves,
      rows,
      existingKeys,
      canSeeSiteIBACriteria,
      shouldShowSiteIBAData
    })
  }

  return rows
}




export const makeCalculationsColumns = (showIBAColumn = false) => {
  const renderHeader = (id) => <FormattedMessage id={id} />

  const headers = [
    {
      id: 'taxonGroup',
      header: renderHeader('taxonGroup'),
      accessorKey: 'taxonGroup',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'season',
      header: renderHeader('season'),
      accessorKey: 'season',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'scientificName',
      header: renderHeader('scientificName'),
      accessorKey: 'scientificName',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'commonName',
      header: renderHeader('commonName'),
      accessorKey: 'commonName',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'iucnStatus',
      header: renderHeader('iucnStatus'),
      accessorKey: 'iucnStatus',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'assessmentParameter',
      header: renderHeader('assessmentParameter'),
      accessorKey: 'assessmentParameter',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'reproductiveUnits',
      header: renderHeader('reproductiveUnits'),
      accessorKey: 'reproductiveUnits',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'population',
      header: renderHeader('population'),
      accessorKey: 'population',
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'kbaCriteria',
      header: renderHeader('kbaCriteria'),
      accessorKey: 'kbaCriteria',
      style: {
        width: '10%'
      },
      cell: ({ getValue }) => getValue()
    }
  ]

  if (showIBAColumn) {
    headers.push({
      id: 'ibaCriterias',
      header: renderHeader('ibaCriterias'),
      accessorKey: 'ibaCriterias',
      cell: ({ getValue }) => getValue()
    })
  }

  return headers
}
